import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { faShieldAlt, faUserShield, faClipboardList } from '@fortawesome/free-solid-svg-icons';

import AuthLayout from '../components/AuthLayout';
import { adminApi, errorMessage } from './adminApi';
import '../pages/providerLogin/styles.css';

const BENEFITS = [
  { icon: faUserShield, text: 'Acesso só para a equipe do Sobre Rodas, com código enviado ao seu e-mail.' },
  { icon: faClipboardList, text: 'Toda ação no painel fica registrada.' },
  { icon: faShieldAlt, text: 'O painel não mostra senhas de clientes nem de oficinas.' },
];

function Spinner({ busy, idle, label }) {
  return busy ? <><span className='auth-spinner' aria-hidden='true' />{label}</> : idle;
}

// Login do painel: e-mail e senha, depois o codigo de 6 digitos enviado por e-mail.
export default function AdminLogin() {
  const history = useHistory();
  const [step, setStep] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const codeRef = useRef(null);

  // Ja logado: vai direto para o painel.
  useEffect(() => {
    let active = true;
    adminApi.me().then(() => { if (active) history.replace('/admin/users'); }).catch(() => {});
    return () => { active = false; };
  }, [history]);

  useEffect(() => {
    if (step === 'code' && codeRef.current) codeRef.current.focus();
  }, [step]);

  const submitPassword = async (event) => {
    event.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.login(email.trim(), password);
      setChallengeId(result.challengeId);
      setPassword('');
      setStep('code');
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível entrar agora. Tente de novo em instantes.'));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError('');
    try {
      await adminApi.verify(challengeId, code);
      window.location.href = '/admin/users';
    } catch (err) {
      setError(errorMessage(err, 'Código inválido ou expirado. Entre de novo.'));
      setBusy(false);
    }
  };

  const submitForgot = async (event) => {
    event.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    setError('');
    try {
      await adminApi.forgotPassword(email.trim());
      setForgotSent(true);
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível enviar agora. Tente de novo em instantes.'));
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep('password');
    setCode('');
    setError('');
    setForgotSent(false);
  };

  return (
    <AuthLayout eyebrow='Painel administrativo' title='Suporte do' highlight='Sobre Rodas.' benefits={BENEFITS}>
      {step === 'forgot' && (
        forgotSent ? (
          <div className='auth-form'>
            <div className='pl-sent' role='status'>
              <h2>Confira seu e-mail</h2>
              <p>Se o e-mail for de uma conta do painel, enviamos um link para criar uma senha nova. Ele vale por 30 minutos.</p>
            </div>
            <button type='button' className='pl-secondary' onClick={restart}>Voltar ao login</button>
          </div>
        ) : (
          <form className='auth-form' onSubmit={submitForgot} noValidate>
            <header className='auth-form-header'>
              <h2>Esqueci a senha</h2>
              <p>Enviaremos um link para o e-mail da sua conta do painel.</p>
            </header>
            <div className='auth-field'>
              <label htmlFor='ad-forgot-email'>E-mail</label>
              <input id='ad-forgot-email' type='email' autoComplete='username' value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} />
            </div>
            {error && <p className='pl-error' role='alert'>{error}</p>}
            <button type='submit' className='auth-submit' disabled={!email || busy} aria-busy={busy}>
              <Spinner busy={busy} idle='Enviar link' label='Enviando…' />
            </button>
            <p className='auth-signup'>
              <button type='button' className='auth-link' onClick={restart}>Voltar ao login</button>
            </p>
          </form>
        )
      )}
      {step === 'password' && (
        <form className='auth-form' onSubmit={submitPassword} noValidate>
          <header className='auth-form-header'>
            <h2>Entrar no painel</h2>
            <p>Use o e-mail e a senha da sua conta de suporte.</p>
          </header>
          <div className='auth-field'>
            <label htmlFor='ad-email'>E-mail</label>
            <input id='ad-email' type='email' autoComplete='username' value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} />
          </div>
          <div className='auth-field'>
            <div className='auth-field-row'>
              <label htmlFor='ad-password'>Senha</label>
              <button type='button' className='auth-link' onClick={() => { setStep('forgot'); setError(''); }}>Esqueci a senha</button>
            </div>
            <input id='ad-password' type='password' autoComplete='current-password' value={password} maxLength={72} onChange={(e) => { setPassword(e.target.value); setError(''); }} />
          </div>
          {error && <p className='pl-error' role='alert'>{error}</p>}
          <button type='submit' className='auth-submit' disabled={!email || !password || busy} aria-busy={busy}>
            <Spinner busy={busy} idle='Continuar' label='Conferindo…' />
          </button>
        </form>
      )}
      {step === 'code' && (
        <form className='auth-form' onSubmit={submitCode} noValidate>
          <header className='auth-form-header'>
            <h2>Digite o código</h2>
            <p>Enviamos um código de 6 dígitos para o seu e-mail. Ele vale por 10 minutos.</p>
          </header>
          <div className='auth-field'>
            <label htmlFor='ad-code'>Código</label>
            <input
              id='ad-code'
              ref={codeRef}
              inputMode='numeric'
              autoComplete='one-time-code'
              className='ad-code'
              value={code}
              maxLength={6}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            />
          </div>
          {error && <p className='pl-error' role='alert'>{error}</p>}
          <button type='submit' className='auth-submit' disabled={code.length !== 6 || busy} aria-busy={busy}>
            <Spinner busy={busy} idle='Entrar' label='Entrando…' />
          </button>
          <p className='auth-signup'>
            Não chegou? <button type='button' className='auth-link' onClick={restart}>Entrar de novo</button>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
